import {NoSsr} from "@material-ui/core";
import {FieldArray, Form, Formik} from "formik";
import {NextSeo} from "next-seo";
import React, {useState} from "react";
import * as Yup from "yup";
import Banner from "../../components/widgets/bannerNew/Banner";
import Button from "../../components/widgets/button/Button";
import Checkbox from "../../components/widgets/checkbox/Checkbox";
import Input from "../../components/widgets/input/Input";
import Section from "../../components/widgets/section/Section";
import Spacer from "../../components/widgets/spacer/Spacer";
import {styled} from "../../styles";
import {Contact as IContact, DayOfWeek} from "../api/contact";
import styles from "./Contact.module.scss";

export type ContactProps = {};

type Child = {
    name: string;
    dob: string;
};

type FormValues = {
    name: string;
    email: string;
    phone: string;
    description: string;
    children?: {
        name: string;
        dob: string;
    }[];
};

const validationSchema = Yup.object({
    name: Yup.string().required("Required"),
    email: Yup.string().email("Invalid Email").required("Required"),
    phone: Yup.string().required("Required"),
    description: Yup.string().required("Required"),
    children: Yup.array<Child>()
        .of(
            Yup.object({
                name: Yup.string().required("Required"),
                dob: Yup.string().required("Required"),
            }).required(),
        )
        .min(0),
});

export default function Contact() {
    const [formSent, setFormSent] = useState(false);

    const onSubmit = async (values: FormValues) => {
        try {
            await global.fetch("/api/contact", {
                method: "POST",
                body: JSON.stringify(values),
            });
            setFormSent(true);
        } catch (e) {
            global.alert("An unknown error has occurred, if this persists please email us at rossmillarswim@xtra.co.nz");
        }
    };

    return (
        <>
            <NextSeo
                title={"Contact"}
                openGraph={{
                    title: "Contact",
                    images: [
                        {
                            url: "/images/classes/tasman_atlantic_pacific_mix/53.jpg",
                        },
                    ],
                }}
            />
            <Banner
                title={"Give your family a life saving skill. \n Register your interest now"}
                image={"/images/classes/tasman_atlantic_pacific_mix/53.jpg"}
            />
            <Section className={styles.contact_content}>
                <p>
                    Enrolments can be made any time throughout the year, however we do recommend you lodge your interest early. Our existing customers do have first priority, with those waiting to join being contacted as spaces become available.  Our waitlist is mainly in the length swimming classes.
                </p>
                <p>
                    If you are unsure of the required level we are happy to arrange a free assessment.
                </p>
            </Section>
            <NoSsr>
                <Section className={styles.contact}>
                    {formSent ? (
                        <label>Thank you for contacting us. We will try our best to get back to you as soon as possible.</label>
                    ) : (
                        <Formik<IContact>
                            onSubmit={onSubmit}
                            validationSchema={validationSchema}
                            initialValues={{
                                name: "",
                                email: "",
                                phone: "",
                                description: "",
                                children: [],
                                availability: [],
                            }}>
                            {({ values, handleSubmit, isSubmitting, isValid, dirty }) => (
                                <Form className={styles.form}>
                                    <label>Contact name</label>
                                    <Input name={"name"} />
                                    <label>Email address</label>
                                    <Input name={"email"} />
                                    <label>Phone</label>
                                    <Input name={"phone"} />
                                    <Spacer spacing={"xs"}/>
                                    <label>Children (Optional)</label>
                                    <FieldArray name={"children"}>
                                        {(arrayHelpers) => (
                                            <Children>
                                                {values.children?.map((child, i) => (
                                                        <div className={styles.child_row} key={i}>
                                                            <Input name={`children[${i}].name`} label={`Child ${i + 1} Name`} />
                                                            <Input
                                                                name={`children[${i}].dob`}
                                                                label={`Child ${i + 1} Date of birth`}
                                                            />
                                                            <Button plain onClick={() => arrayHelpers.remove(i)}>
                                                                - Remove Child
                                                            </Button>
                                                        </div>
                                                    ))}

                                                <ChildButton
                                                    plain
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            name: "",
                                                            dob: "",
                                                        })
                                                    }>
                                                    + Add Child
                                                </ChildButton>
                                            </Children>
                                        )}
                                    </FieldArray>
                                    <label>Availability (Optional)</label>
                                    <FieldArray name={"availability"}>
                                        {(arrayHelpers) => (
                                            <CheckboxRow>
                                                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((dayOfWeek) => {

                                                    const onChange = (checked: boolean) => {
                                                        const index = values.availability.findIndex((dow) => dow === dayOfWeek.toUpperCase());
                                                        if (!checked) {
                                                            arrayHelpers.remove(index);
                                                        } else {
                                                            arrayHelpers.push(dayOfWeek.toUpperCase());
                                                        }
                                                    };

                                                    return (
                                                        <Checkbox label={dayOfWeek}
                                                                  onChange={onChange}
                                                                  checked={values.availability.includes(dayOfWeek.toUpperCase() as DayOfWeek)} />
                                                    )
                                                })}
                                            </CheckboxRow>
                                        )}
                                    </FieldArray>
                                    <Input name={"description"} label={"Brief description of swimming ability"} multiline />
                                    <Button disabled={isSubmitting || !isValid || !dirty} onClick={() => handleSubmit()}>
                                        {isSubmitting ? "SENDING..." : "SUBMIT"}
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Section>
            </NoSsr>
        </>
    );
}

const CheckboxRow = styled.div`
    flex-direction: row;
`;

const Children = styled.div`
    width: 100%;
    position: relative;
    
    > * {
        margin-bottom: 18px;
    }
`;

const ChildButton = styled(Button)`
    width: 100%;
`;
